import React, { useState, useEffect } from 'react';
import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input/mobile';
import PhoneInput from 'react-phone-number-input/mobile';
import { observer } from 'mobx-react-lite';

import { useAnalytics } from '../../../utils/analytics';
import { sendGTMEvent } from '../../../utils/googleTagManager';
import { isMobileAppWebView } from '../../../utils/utilsFunctions';
import { ActionName } from '../../../utils/mobileApp/mobileApp.types';
import { goBackToMobileApp } from '../../../utils/mobileApp';

import { useMst } from '../../../models/rootModel';
import { SignUpData } from '../../../models/types/UserDiscoveryResponse.types';
import { SignUpErrorPopUp } from './SignUpErrorPopUp';

import { StepWrapperBox } from '../../styledComponent/Boxes.style';
import { SignUpBottomTextInput } from '../../styledComponent/Text.style';

import { BaseStep, TitleAndDescriptionProps, StepProps, StepWizardProps } from '../../common/BaseStep';
import { InputRoot, Label, ErrorInputText } from '../../styledComponent/SignUpComponents.style';
import { SignUpFields, ErrorCode, ServerError } from './SignUp.types';

export interface SignUpValues {
  [SignUpFields.Phone]: string;
}

export interface SignUpPhoneStepProps extends TitleAndDescriptionProps, StepProps, StepWizardProps {
  inputFields: {
    type: 'phone';
    label: string;
    placeHolder: string;
    explanation?: string;
  };
}

export const SignUpPhoneStep = observer((props: SignUpPhoneStepProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberInputError, setPhoneNumberInputError] = useState('');
  const [serverErrorMessage, setServerErrorMessage] = useState<ServerError>();
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { inputFields, nextStep, isActive, stepType } = props;
  const { UserDiscoveryResponse, Step, UserPayment, Group } = useMst();

  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      analytics.track('view signup step', { referringUrl: document.referrer })
      window.scrollTo(0, 0)
    }
  }, [isActive, analytics]);

  const phoneChange = (value: string | undefined) => {
    setPhoneNumberInputError('');
    if (typeof value === 'string') {
      setPhoneNumber(value);
      if (value.length > 0) {
        setIsButtonDisabled(false);
        return;
      }
    }
    setIsButtonDisabled(true);
  };

  const sendData = async () => {
    const dataToSend: SignUpData = UserDiscoveryResponse.signUpDateToSend;

    if (UserPayment.appliedCouponCode) {
      dataToSend.couponCode = UserPayment.appliedCouponCode;
    }

    try {
      const resSignUpUser = await UserDiscoveryResponse.signUpUser({ ...dataToSend, origin: Step.origin ?? '' });
      if (resSignUpUser.kind !== 'ok') {
        throw new Error('Welcome back! We found an existing account with your contact details.');
      }

      const { accountId } = resSignUpUser.data;

      sendGTMEvent({
        event: 'signup_completed',
        user_id: accountId,
        phone: dataToSend.phone,
        full_name: dataToSend.name,
        email: dataToSend.email,
      });

      analytics.track(`click signUp complete ${props.type}`, {
        stepId: props.id,
        stepType: props.type,
        stepIndex: props.stepIndex,
        stepTitle: props.title ?? '',
        ...UserDiscoveryResponse.utms,
        origin: Step.origin,
        userID: UserDiscoveryResponse.accountId,
      });

      Group.findGroupsOptions();

      if (isMobileAppWebView) {
        goBackToMobileApp({ accessToken: localStorage.getItem('authToken'), action: ActionName.Silent_Login });
      }

      if (props.totalSteps !== props.stepIndex + 1) {
        nextStep();
      }
    } catch (error) {
      if (error instanceof Error) {
        setServerErrorMessage({ message: error.message, errorCode: ErrorCode.Exists });
        setIsPopUpOpen(true);
      }
    }
  };

  const onSubmit = () => {
    const parsedPhone = parsePhoneNumber(phoneNumber);
    UserDiscoveryResponse.setUserPhoneNumber(phoneNumber);
    if (parsedPhone) {
      const params = {
        phone: parsedPhone.nationalNumber,
        country_code: `+${parsedPhone.countryCallingCode}`,
        email: UserDiscoveryResponse.userEmail,
      };
      const urlParams = new URLSearchParams(params);
      setIsLoading(true);
      UserDiscoveryResponse.canUserSignUp(urlParams.toString())
        .then((res) => {
          if (res.kind === 'ok') {
            sendData();
            return;
          }
          throw new Error(res.data.error || ErrorCode.InternalServerError);
        })
        .catch((error) => {
          const message =
            error.message === ErrorCode.Exists
              ? 'Welcome back! We found an existing account with your contact details. Please click on the button below to log back in.'
              : 'Sorry, something went wrong...';
          setServerErrorMessage({ message, errorCode: ErrorCode.Exists });
          setIsPopUpOpen(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const validatedSignUpInformation = () => {
    setIsButtonDisabled(true);
    analytics.track('signup confirm click', { stepType })
    if (!isValidPhoneNumber(phoneNumber)) {
      setPhoneNumberInputError('Please enter your correct phone number');
      return;
    }

    onSubmit();
  };

  const onClosePopUp = () => {
    setIsPopUpOpen(false);
    if (serverErrorMessage?.errorCode === ErrorCode.Exists) {
      window.open(process.env.REACT_APP_API_LOGIN, '_self');
    }
  };

  return (
    <>
      {isActive ? (
        <BaseStep
          {...props}
          isLoading={isLoading}
          isButtonDisabled={isButtonDisabled}
          changeButtonFunction={validatedSignUpInformation}
        >
          <StepWrapperBox>
            <InputRoot>
              <Label>{inputFields.label}</Label>
              <PhoneInput
                // identify element for e2e test
                id="signUpPhoneFormPhone"
                onChange={(value: string | undefined) => phoneChange(value)}
                value={phoneNumber}
                placeholder={inputFields.placeHolder}
                defaultCountry={'US'}
                maxLength={16}
              />
              {inputFields.explanation && phoneNumberInputError.length === 0 ? (
                <SignUpBottomTextInput>{inputFields.explanation}</SignUpBottomTextInput>
              ) : (
                ''
              )}
              {phoneNumberInputError.length > 0 ? <ErrorInputText>{phoneNumberInputError}</ErrorInputText> : null}
            </InputRoot>
          </StepWrapperBox>

          <SignUpErrorPopUp
            setIsPopUpOpen={setIsPopUpOpen}
            isPopUpOpen={isPopUpOpen}
            onClosePopUp={onClosePopUp}
            serverErrorMessage={serverErrorMessage}
          />
        </BaseStep>
      ) : (
        ''
      )}
    </>
  );
});
